/**
 * Admin → Feedback: the user feedback inbox (feedback collection).
 * Newest first, unread-first toggle, screenshot viewing (presigned URL),
 * and a mark-read action.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getAdminFeedback,
  markFeedbackRead,
  type FeedbackItem,
} from "@/features/admin/lib/adminApi";
import { relativeTime } from "@/shared/format";

export function FeedbackView() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-feedback", unreadOnly],
    queryFn: () => getAdminFeedback(unreadOnly),
  });

  return (
    <div className="space-y-5">
      <label className="flex items-center gap-2 text-sm text-text-muted">
        <input
          type="checkbox"
          checked={unreadOnly}
          onChange={(e) => setUnreadOnly(e.target.checked)}
          className="accent-accent"
        />
        Unread only
      </label>

      {isLoading && <p className="text-sm text-text-muted">Loading feedback…</p>}
      {isError && <p className="text-sm text-danger">Couldn't load feedback.</p>}
      {data && data.length === 0 && (
        <p className="text-sm text-text-muted">No feedback{unreadOnly ? " unread" : ""}.</p>
      )}
      {data?.map((item) => <FeedbackRow key={item.id} item={item} />)}
    </div>
  );
}

function FeedbackRow({ item }: { item: FeedbackItem }) {
  const qc = useQueryClient();
  const markRead = useMutation({
    mutationFn: () => markFeedbackRead(item.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-feedback"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
  });
  const isUnread = item.readAt === null;

  return (
    <section
      className={[
        "rounded-2xl border bg-surface p-5 space-y-3",
        isUnread ? "border-accent/40" : "border-border",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-text whitespace-pre-wrap flex-1">{item.text}</p>
        {isUnread && (
          <span className="text-[11px] rounded-full bg-accent/15 text-accent px-2 py-0.5 font-medium shrink-0">
            new
          </span>
        )}
      </div>

      {item.screenshotUrl && (
        <a href={item.screenshotUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={item.screenshotUrl}
            alt="feedback screenshot"
            className="max-h-48 rounded-lg border border-border"
          />
        </a>
      )}

      <div className="flex items-center justify-between gap-3 text-xs text-text-muted">
        <span className="truncate">
          {item.pageUrl ?? "unknown page"}
          {item.createdAt ? ` · ${relativeTime(item.createdAt)}` : ""}
        </span>
        {isUnread ? (
          <button
            type="button"
            disabled={markRead.isPending}
            onClick={() => markRead.mutate()}
            className="shrink-0 rounded-lg border border-border px-3 py-1.5 font-medium text-text-muted hover:bg-text/5 hover:text-text transition disabled:opacity-40"
          >
            {markRead.isPending ? "…" : "Mark read"}
          </button>
        ) : (
          <span className="shrink-0 text-text-muted/60">
            read{item.readAt ? ` ${relativeTime(item.readAt)}` : ""}
          </span>
        )}
      </div>
    </section>
  );
}
